/**
 * OzPol Transparent — Party Detail View
 * Shows full funding breakdown for a single political party.
 */
import React from "react";
import {
  PieChart, Pie, Cell, Tooltip as RechartsTip, ResponsiveContainer,
} from "recharts";
import { DollarSign, ExternalLink, Loader2, Zap } from "lucide-react";
import { fmt, HHI_STYLE } from "../../utils/donationsUtils";
import { PieTip, Stat } from "./DonationsHelpers";

export default function PartyDetailView({ detail, year, isDark, explainStates, onExplain }) {
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text    = isDark ? "#F8FAFC" : "#0F172A";
  const muted   = isDark ? "#94A3B8" : "#6B7280";
  const bdr     = isDark ? "#334155" : "#000000";
  const hhi     = HHI_STYLE[detail.hhi_label] || HHI_STYLE.Diversified;

  return (
    <div data-testid="party-detail-view">
      {/* Party header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          background: detail.party.color, color: "#fff", padding: "6px 14px",
          fontWeight: 900, fontSize: 18, fontFamily: "Outfit, sans-serif",
          border: "2px solid #000", boxShadow: "3px 3px 0 #000",
        }}>{detail.party.abbreviation}</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: text }}>{detail.party.name}</div>
          <div style={{ fontSize: 11, color: muted }}>{detail.party.dominant_funding_type} · FY {year}</div>
        </div>
        <div style={{
          marginLeft: "auto", fontSize: 10, fontWeight: 700, padding: "4px 10px",
          border: `2px solid ${hhi.border}`, background: hhi.bg, color: hhi.color,
          whiteSpace: "nowrap",
        }} data-testid="hhi-badge">
          {detail.hhi_label} · HHI {detail.hhi.toLocaleString()}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <Stat label="TOTAL DECLARED INCOME" value={fmt(detail.total_declared_aud)} sub={`FY ${year}`} highlight isDark={isDark} />
        <Stat label="TOP 3 DONOR SHARE" value={`${detail.top3_concentration_pct}%`} sub="of declared total" isDark={isDark} />
        <Stat label="TOP 10 DONOR SHARE" value={`${detail.top10_concentration_pct}%`} sub="of declared total" isDark={isDark} />
        <Stat label="DECLARED DONORS" value={detail.num_declared_donors} sub="above AEC threshold" isDark={isDark} />
      </div>

      {/* Description */}
      <div style={{
        borderLeft: `5px solid ${detail.party.color}`, border: "2px solid",
        borderColor: bdr, borderLeftColor: detail.party.color,
        padding: 14, marginBottom: 20,
        background: isDark ? "#0F172A" : "#FAFAFA",
      }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: muted, letterSpacing: "0.07em", marginBottom: 6 }}>FUNDING ANALYSIS</div>
        <p style={{ fontSize: 13, color: text, lineHeight: 1.7, margin: 0 }}>{detail.party.description}</p>
      </div>

      {/* Chart + bars */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        {/* Donut chart */}
        <div style={{
          border: "2px solid #000", padding: 16, background: surface,
          boxShadow: "3px 3px 0 #000", flex: "0 0 270px",
        }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: muted, letterSpacing: "0.07em", marginBottom: 8 }}>
            INCOME BREAKDOWN
          </div>
          <div style={{ position: "relative" }}>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie
                  data={detail.chart_data} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" innerRadius={52} outerRadius={84} paddingAngle={2}
                >
                  {detail.chart_data.map((e, i) => (
                    <Cell key={i} fill={e.color} stroke="#000" strokeWidth={1} />
                  ))}
                </Pie>
                <RechartsTip content={<PieTip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{
              position: "absolute", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: muted }}>TOTAL</div>
              <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: text }}>
                {fmt(detail.total_declared_aud)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
            {detail.chart_data.slice(0, 6).map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 9, height: 9, background: d.color, border: "1px solid #000", flexShrink: 0 }} />
                <div style={{ fontSize: 10, color: text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.name}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: muted }}>{d.pct}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Horizontal bars */}
        <div style={{
          border: "2px solid #000", padding: 16, background: surface,
          boxShadow: "3px 3px 0 #000", flex: 1, minWidth: 280,
        }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: muted, letterSpacing: "0.07em", marginBottom: 14 }}>
            TOP DONORS BY DECLARED AMOUNT
          </div>
          {detail.donors.slice(0, 8).map((d) => (
            <div key={d.donor_id} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.donor_name}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: text, whiteSpace: "nowrap" }}>{fmt(d.amount_aud)}</span>
              </div>
              <div style={{ background: isDark ? "#0F172A" : "#E5E7EB", height: 10, border: "1px solid #000", position: "relative" }}>
                <div style={{
                  width: `${d.percentage_share}%`, height: "100%",
                  background: d.color, transition: "width 0.5s ease",
                }} />
              </div>
              <div style={{ fontSize: 9, color: muted, marginTop: 2 }}>
                {d.percentage_share}% · {d.type}{d.parent_entity ? ` · ${d.parent_entity}` : ""}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Donor table */}
      <div style={{ border: "2px solid #000", boxShadow: "4px 4px 0 #000", overflow: "hidden" }}>
        <div style={{
          padding: "12px 16px", background: "#0F172A", color: "#fff",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <DollarSign style={{ width: 15, height: 15, color: "#FFC82E" }} />
          <span style={{ fontWeight: 800, fontSize: 13, fontFamily: "Outfit, sans-serif" }}>
            DECLARED FUNDING — {detail.party.name.toUpperCase()} — FY {year}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="donors-table">
            <thead>
              <tr style={{ background: isDark ? "#1E293B" : "#F3F4F6" }}>
                {["#", "Donor", "Category", "Amount", "Share %", "Type", "AI Explain"].map(h => (
                  <th key={h} style={{
                    padding: "10px 12px", fontSize: 9, fontWeight: 800,
                    letterSpacing: "0.07em", textAlign: "left",
                    borderBottom: "2px solid #000", color: text, whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.donors.map((d, i) => {
                const key = `${d.donor_id}::${detail.party.id}::${year}`;
                const es = explainStates[key];
                return (
                  <React.Fragment key={d.donor_id}>
                    <tr style={{
                      background: i % 2 === 0 ? surface : (isDark ? "#0F172A" : "#F9F7F2"),
                      borderBottom: es?.expanded ? "none" : `1px solid ${isDark ? "#334155" : "#E5E7EB"}`,
                    }}>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 800, color: muted }}>{i + 1}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: text }}>{d.donor_name}</div>
                            {d.parent_entity && (
                              <div style={{ fontSize: 9, color: muted }}>Part of {d.parent_entity}</div>
                            )}
                            <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
                              {d.website && (
                                <a href={d.website} target="_blank" rel="noopener noreferrer"
                                  data-testid={`donor-website-link-${d.donor_id}`}
                                  onClick={e => e.stopPropagation()}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 3,
                                    fontSize: 9, fontWeight: 700, color: "#FFC82E",
                                    textDecoration: "none", padding: "1px 0",
                                  }}>
                                  <ExternalLink style={{ width: 8, height: 8 }} /> Website
                                </a>
                              )}
                              <a href="https://transparency.aec.gov.au/" target="_blank" rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 3,
                                  fontSize: 9, fontWeight: 700, color: muted,
                                  textDecoration: "none", padding: "1px 0",
                                }}>
                                <ExternalLink style={{ width: 8, height: 8 }} /> AEC
                              </a>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: "2px 7px",
                          border: `2px solid ${d.color}`, color: d.color,
                          background: `${d.color}18`, whiteSpace: "nowrap",
                        }}>{d.category}</span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 800, fontFamily: "Outfit, sans-serif", color: text, whiteSpace: "nowrap" }}>
                        {fmt(d.amount_aud)}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{
                            width: `${Math.min(d.percentage_share, 100) * 0.75}px`,
                            height: 6, maxWidth: 75, background: d.color, border: "1px solid #000", flexShrink: 0,
                          }} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: text }}>{d.percentage_share}%</span>
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: muted,
                          padding: "2px 6px", border: `1px solid ${bdr}`,
                          whiteSpace: "nowrap",
                        }}>{d.type}</span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          data-testid={`explain-btn-${d.donor_id}`}
                          onClick={() => onExplain(d.donor_id, detail.party.id, year, d.amount_aud)}
                          disabled={es?.loading}
                          style={{
                            display: "flex", alignItems: "center", gap: 4,
                            padding: "4px 9px", fontSize: 9, fontWeight: 800,
                            border: "2px solid",
                            borderColor: es?.text ? "#FFC82E" : "#000",
                            background: es?.text ? "#FFC82E" : (es?.loading ? "#374151" : "#0F172A"),
                            color: es?.text ? "#000" : "#FFC82E",
                            cursor: es?.loading ? "wait" : "pointer",
                            fontFamily: "Outfit, sans-serif", whiteSpace: "nowrap",
                          }}>
                          {es?.loading ? (
                            <Loader2 style={{ width: 9, height: 9 }} className="animate-spin" />
                          ) : (
                            <Zap style={{ width: 9, height: 9 }} />
                          )}
                          {es?.text ? (es.expanded ? "HIDE" : "SHOW") : "AI EXPLAIN"}
                        </button>
                      </td>
                    </tr>
                    {es?.expanded && (
                      <tr style={{
                        background: isDark ? "#0F172A" : "#FFFBEB",
                        borderBottom: `1px solid ${isDark ? "#334155" : "#E5E7EB"}`,
                      }}>
                        <td colSpan={7} style={{ padding: "12px 16px" }}>
                          <div style={{ borderLeft: "4px solid #FFC82E", paddingLeft: 12 }}>
                            {es.loading ? (
                              <span style={{ color: muted, fontSize: 13 }}>Generating AI analysis...</span>
                            ) : (
                              <>
                                <div style={{ fontSize: 9, fontWeight: 800, color: "#F59E0B", marginBottom: 6, letterSpacing: "0.06em" }}>
                                  AI ANALYSIS — GPT-4o
                                </div>
                                <p style={{ fontSize: 13, color: text, lineHeight: 1.7, margin: 0 }}>{es.text}</p>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "8px 16px", background: isDark ? "#1E293B" : "#F9F7F2", borderTop: `1px solid ${isDark ? "#334155" : "#E5E7EB"}` }}>
          <p style={{ fontSize: 9, color: muted, margin: 0 }}>
            Source: AEC Transparency Register (transparency.aec.gov.au). Declared receipts above AEC disclosure threshold (~$16,900 in 2023-24). Total party income is higher.
          </p>
        </div>
      </div>
    </div>
  );
}
